import type { ProjectDefinition } from "../../../types";
import { type FuncionProjectSpec, NOMBRES, conditions, seededRandom } from "../spec";
import { large, percentileInc, rankAvg, rankEq } from "./estadistica";

const SHEET = "MT Concurso";
const TABLE = "Concurso";
const REGIONES = ["Norte", "Sur", "Centro"] as const;

const rnd = seededRandom(9494);
// Vendedor, Región, Ventas, Devoluciones %, Satisfacción (1–10, with ties)
const ROWS = NOMBRES.slice(0, 24).map((nombre, i) => [
  nombre,
  REGIONES[i % REGIONES.length],
  350000 + i * 4130 + rnd.int(0, 90) * 1000,
  rnd.int(5, 120) / 1000,
  rnd.int(6, 10),
] as [string, string, number, number, number]);

type Row = (typeof ROWS)[number];
const ventas = ROWS.map((r: Row) => r[2]);
const devol = ROWS.map((r) => r[3]);
const satis = ROWS.map((r) => r[4]);
const tercero = large(ventas, 3);
const p75 = percentileInc(ventas, 0.75);

export const spec: FuncionProjectSpec = {
  projectId: "funciones-jerarquia",
  sheets: [
    {
      name: SHEET,
      title: "Concurso de ventas",
      subtitle: "Resultados del trimestre — arma el ranking oficial del concurso",
      tables: [
        {
          name: TABLE,
          anchor: "A4",
          headers: ["Vendedor", "Región", "Ventas", "Devoluciones", "Satisfacción"],
          rows: ROWS,
          answerColumns: ["Lugar ventas", "Lugar devoluciones", "Lugar satisfacción", "Top 25%", "Lugar en región"],
          formats: { Ventas: "$#,##0", Devoluciones: "0.0%" },
        },
      ],
      answers: [
        { address: "M4", label: "Tarea 4 · 3.ª venta más alta", format: "$#,##0" },
        { address: "M5", label: "Tarea 5 · Vendedor en 3.er lugar" },
      ],
    },
  ],
  checks: {
    1: { kind: "column", sheet: SHEET, table: TABLE, column: "Lugar ventas", fns: ["RANK.EQ"], expected: ventas.map((v) => rankEq(v, ventas)) },
    2: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Lugar devoluciones", fns: ["RANK.EQ"],
      expected: devol.map((v) => rankEq(v, devol, true)),
    },
    3: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Lugar satisfacción", fns: ["RANK.AVG"],
      expected: satis.map((v) => rankAvg(v, satis)),
    },
    4: { kind: "cell", sheet: SHEET, address: "M4", fns: ["LARGE"], expected: tercero },
    5: {
      kind: "cell", sheet: SHEET, address: "M5", fns: ["INDEX|XLOOKUP", "LARGE"],
      expected: ROWS.find((r) => r[2] === tercero)![0],
    },
    6: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Top 25%", fns: ["IF", "PERCENTILE.INC"], exact: true,
      expected: ROWS.map((r) => (r[2] >= p75 ? "Top 25%" : "")),
    },
    7: {
      kind: "column", sheet: SHEET, table: TABLE, column: "Lugar en región", fns: ["COUNTIFS"],
      expected: ROWS.map((r) => ROWS.filter((o) => o[1] === r[1] && o[2] > r[2]).length + 1),
    },
  },
};

const F = (text: string) => `<span class="formula">${text}</span>`;

export const project: ProjectDefinition = {
  id: "funciones-jerarquia",
  title: "JERARQUIA.EQV: Concurso de ventas",
  subtitle: "Rankings, empates y lugares dentro de cada grupo",
  description:
    "Organizas el concurso trimestral de 24 vendedores. Hay que asignar lugares por ventas (más es mejor), por devoluciones (menos es mejor) y por satisfacción (donde hay empates), señalar el 25% superior y dar a cada uno su lugar dentro de su región.\n\nJERARQUIA.EQV da el lugar de un valor dentro de una lista. Sin tercer argumento, el valor más grande es el lugar 1; con 1, el más pequeño.",
  topics: [
    "JERARQUIA.EQV descendente y ascendente",
    "Empates: JERARQUIA.EQV frente a JERARQUIA.MEDIA",
    "K.ESIMO.MAYOR: el valor en el lugar N",
    "Quién está en el lugar N",
    "Top 25% con PERCENTIL.INC",
    "Lugar dentro de un grupo con CONTAR.SI.CONJUNTO",
  ],
  tasks: [
    {
      id: 1,
      level: "basico",
      title: "Tarea 1: Lugar por ventas",
      description: "En la hoja <strong>MT Concurso</strong>, columna <strong>Lugar ventas</strong>: el lugar de cada vendedor por <strong>Ventas</strong> (1 = quien más vendió).",
      solution: [
        `Selecciona ${F("F5")}.`,
        "JERARQUIA.EQV(número, lista). Sin tercer argumento, ordena de mayor a menor.",
        `Escribe ${F("=JERARQUIA.EQV([@Ventas],[Ventas])")}.`,
      ],
      expectedResult: "1 a 24",
      finalFormula: "=JERARQUIA.EQV([@Ventas],[Ventas])",
    },
    {
      id: 2,
      level: "basico",
      title: "Tarea 2: Lugar por devoluciones",
      description: "En la columna <strong>Lugar devoluciones</strong>: aquí <strong>menos es mejor</strong>. El lugar 1 es quien tiene el porcentaje de devoluciones <strong>más bajo</strong>.",
      solution: [
        `Selecciona ${F("G5")}.`,
        `El tercer argumento, orden, con ${F("1")} pone el valor más pequeño en el lugar 1.`,
        `Escribe ${F("=JERARQUIA.EQV([@Devoluciones],[Devoluciones],1)")}.`,
      ],
      expectedResult: "1 = menos devoluciones",
      finalFormula: "=JERARQUIA.EQV([@Devoluciones],[Devoluciones],1)",
    },
    {
      id: 3,
      level: "intermedio",
      title: "Tarea 3: Lugares con empates",
      description: conditions(
        "En la columna <strong>Lugar satisfacción</strong>: varios vendedores tienen la misma calificación. Si dos empatan en los lugares 3 y 4:",
        [
          "JERARQUIA.EQV les daría <strong>3</strong> a los dos y nadie quedaría en el 4",
          "JERARQUIA.MEDIA les da <strong>3.5</strong> a cada uno (el promedio de los lugares que ocupan)",
        ],
        "Usa <strong>JERARQUIA.MEDIA</strong>, que es la forma justa de repartir los puntos de un concurso."
      ),
      solution: [`Selecciona ${F("H5")}.`, `Escribe ${F("=JERARQUIA.MEDIA([@Satisfacción],[Satisfacción])")}.`],
      expectedResult: "Lugares con .5 donde hay empates",
      finalFormula: "=JERARQUIA.MEDIA([@Satisfacción],[Satisfacción])",
    },
    {
      id: 4,
      level: "intermedio",
      title: "Tarea 4: La tercera venta más alta",
      description: "En <strong>M4</strong>: el monto de ventas del <strong>3.er lugar</strong>.",
      solution: [
        `Selecciona ${F("M4")}.`,
        "K.ESIMO.MAYOR(lista, k) devuelve el k-ésimo valor más grande. Su par es K.ESIMO.MENOR.",
        `Escribe ${F("=K.ESIMO.MAYOR(Concurso[Ventas],3)")}.`,
      ],
      expectedResult: "Ventas del 3.er lugar",
      finalFormula: "=K.ESIMO.MAYOR(Concurso[Ventas],3)",
    },
    {
      id: 5,
      level: "intermedio",
      title: "Tarea 5: ¿Quién quedó en 3.er lugar?",
      description: "En <strong>M5</strong>: el <strong>nombre</strong> del vendedor en 3.er lugar por ventas.",
      solution: [
        `Selecciona ${F("M5")}.`,
        "K.ESIMO.MAYOR da el monto; COINCIDIR busca en qué fila está; INDICE trae el nombre de esa fila.",
        `Escribe ${F("=INDICE(Concurso[Vendedor],COINCIDIR(K.ESIMO.MAYOR(Concurso[Ventas],3),Concurso[Ventas],0))")}.`,
      ],
      expectedResult: "Nombre del 3.er lugar",
      finalFormula: "=INDICE(Concurso[Vendedor],COINCIDIR(K.ESIMO.MAYOR(Concurso[Ventas],3),Concurso[Ventas],0))",
    },
    {
      id: 6,
      level: "reto",
      title: "Tarea 6: Top 25%",
      description: conditions("En la columna <strong>Top 25%</strong>:", [
        "Si las Ventas son <strong>mayores o iguales</strong> al percentil 75 → <strong>Top 25%</strong>",
        "Si no → celda <strong>vacía</strong> (\"\")",
      ]),
      solution: [`Selecciona ${F("I5")}.`, `Escribe ${F('=SI([@Ventas]>=PERCENTIL.INC([Ventas],75%),"Top 25%","")')}.`],
      expectedResult: "6 vendedores marcados",
      finalFormula: '=SI([@Ventas]>=PERCENTIL.INC([Ventas],75%),"Top 25%","")',
    },
    {
      id: 7,
      level: "reto",
      title: "Tarea 7: Lugar dentro de su región",
      description: conditions(
        "En la columna <strong>Lugar en región</strong>: el lugar de cada vendedor comparado solo con los de <strong>su misma región</strong>. JERARQUIA.EQV no acepta condiciones, así que:",
        [
          "Cuenta cuántos vendedores de la misma región vendieron <strong>más</strong> que él",
          "Súmale 1",
        ]
      ),
      solution: [
        `Selecciona ${F("J5")}.`,
        `Escribe ${F('=CONTAR.SI.CONJUNTO([Región],[@Región],[Ventas],">"&[@Ventas])+1')}.`,
        "Este truco funciona para rankings por grupo: por región, por sucursal, por mes…",
      ],
      expectedResult: "1 a 8 dentro de cada región",
      finalFormula: '=CONTAR.SI.CONJUNTO([Región],[@Región],[Ventas],">"&[@Ventas])+1',
    },
  ],
};
